import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { sidebarActive } from '@Lib/slices/uiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { ChevronDown } from 'react-feather';
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  NavLink,
} from 'reactstrap';

function MenuItemHasChild({ item, handleClickItem }) {
  const accessPermisions = useSelector((state) => state.user.accessPermisions);
  const userInfo = useSelector((state) => state.user.info);
  const routeMatch = useRouteMatch();
  const dispatch = useDispatch();

  useEffect(() => {
    const childMenu = item.child.find((childRoute) => childRoute.path === routeMatch.path);
    if (childMenu) {
      dispatch(sidebarActive(item.id));
    }
  }, []);

  const validAccess = (menu) => {
    const accessName = menu.accessName;
    if (!accessName) {
      return true;
    }

    const rolesByProject = userInfo?.roles_by_project || {};
    const allRoles = Object.values(rolesByProject).flatMap((roles) =>
      Array.isArray(roles) ? roles : [],
    );
    const isPrivileged = Boolean(
      userInfo?.is_super_admin ||
        allRoles.some((role) => String(role).toLowerCase().includes('admin')),
    );

    if (isPrivileged) {
      return true;
    }

    if (accessPermisions && accessPermisions[`${menu.accessName}`]) {
      return true;
    }

    return false;
  };

  const renderMenuIcon = (menu) => {
    if (menu.icon) {
      if (typeof menu.icon === 'string') {
        return (
          <span
            className="d-flex align-items-center"
            dangerouslySetInnerHTML={{ __html: menu.icon }}
          />
        );
      }

      return <span className="d-flex align-items-center">{menu.icon}</span>;
    }

    return <div className="icon-default">{menu.name.charAt(0)}</div>;
  };

  const renderMenuItem = (menu) => (
    <DropdownItem
      onClick={() => handleClickItem(menu)}
      tag="a"
      key={menu.id}
      className="d-flex align-items-center"
    >
      <div className="menu-icon-wrapper">{renderMenuIcon(menu)}</div>
      <span className="menu-title">{menu.name}</span>
      {menu.badge && <span className="badge badge-pill badge-danger ml-50">{menu.badge}</span>}
    </DropdownItem>
  );

  const visibleChildren = item.child.filter((menu) => !menu.hidden && validAccess(menu));

  const groupedChildren = visibleChildren.reduce((accumulator, menu) => {
    const groupKey = menu.group || 'default';

    if (!accumulator[groupKey]) {
      accumulator[groupKey] = [];
    }

    accumulator[groupKey].push(menu);
    return accumulator;
  }, {});

  const fallbackColumns = Object.keys(groupedChildren).map((groupKey) => ({
    key: groupKey,
    title: null,
  }));

  let menuColumns = fallbackColumns;
  if (item.menuColumns?.length) {
    menuColumns = item.menuColumns.map((column) => ({
      key: column.key,
      title: column.title,
    }));
  }

  const isTwoColumnMenu = item.menuLayout === 'two-column';

  return item.isSubMenu ? (
    <UncontrolledDropdown tag="li" className="nav-item">
      <DropdownToggle
        nav
        className={`menu-item-content d-flex align-items-center ${
          item.isActive ? 'active' : ''
        }`}
      >
        {item.icon && <span className="mr-50 d-flex align-items-center">{item.icon}</span>}
        <span className="d-flex align-items-center" style={{ gap: '4px' }}>
          {item.name}
          {item.isSubMenu && <ChevronDown size={14} />}
        </span>
      </DropdownToggle>
      <DropdownMenu
        modifiers={{
          preventOverflow: { boundariesElement: 'window' },
          flip: { behavior: ['bottom-start', 'bottom-end', 'top-start', 'top-end'] },
        }}
        className={`dropdown-menu-custom ${isTwoColumnMenu ? 'dropdown-menu-two-column' : ''}`}
      >
        {isTwoColumnMenu ? (
          <div className="menu-two-column-grid">
            {menuColumns.map((column) => {
              const menus = groupedChildren[column.key] || [];

              if (!menus.length) {
                return null;
              }

              return (
                <div className="menu-column" key={column.key}>
                  {column.title ? <div className="menu-column-title">{column.title}</div> : null}
                  {menus.map((menu) => renderMenuItem(menu))}
                </div>
              );
            })}
          </div>
        ) : (
          visibleChildren.map((menu) => renderMenuItem(menu))
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  ) : (
    <li className="nav-item">
      <NavLink
        onClick={() => handleClickItem(item)}
        className={`menu-item-content d-flex align-items-center ${item.isActive ? 'active' : ''}`}
      >
        {item.icon && <span className="mr-50 d-flex align-items-center">{item.icon}</span>}
        {item.name}
      </NavLink>
    </li>
  );
}

MenuItemHasChild.propTypes = {
  item: PropTypes.object,
  handleClickItem: PropTypes.func,
};

export default MenuItemHasChild;
